/**
 * Section "Documents" de la page detail :
 * grille des cartes, 1 par doc_type. Gere :
 *  - validate / reject (avec modal raison)
 *  - upload manuel
 *  - supersede (remplacer)
 *  - preview PDF inline
 *
 * Dispatch les mutations via les callbacks props (parent gere l etat dossier).
 */

import { useState, useMemo } from 'react';
import DocCard from '../components/DocCard.jsx';
import RejectModal from '../components/RejectModal.jsx';
import { DOC_TYPE_META } from '../lib/docTypes.js';
import { normalizeDocuments } from '../lib/normalizeDocument.js';
import * as ceeApi from '../../api/cee.js';

/**
 * @param {{
 *   dossier: object | null,
 *   onChange: () => Promise<void> | void,
 *   readOnly?: boolean,
 * }} props
 */
export default function DocumentsSection({ dossier, onChange, readOnly = false }) {
  const [busyDocId, setBusyDocId] = useState(null);
  const [previewKey, setPreviewKey] = useState(null);
  const [rejectModal, setRejectModal] = useState({ open: false, doc: null, label: '' });
  const [error, setError] = useState('');

  // Codex S2.6 P1 : normalize snake_case -> camelCase pour matcher peu importe
  // que le backend renvoie doc_type/is_current ou docType/isCurrent.
  const docs = useMemo(() => normalizeDocuments(dossier?.documents), [dossier?.documents]);

  if (!dossier) return null;

  /** @param {string} typeKey @returns {object | null} */
  const getDocByType = (typeKey) => docs.find((d) => d.docType === typeKey && d.isCurrent !== false) || null;

  /** @param {() => Promise<unknown>} fn */
  const wrapBusy = async (docId, fn) => {
    setBusyDocId(docId);
    setError('');
    try {
      await fn();
      await onChange();
    } catch (err) {
      setError(err.message || 'Action echouee');
    } finally {
      setBusyDocId(null);
    }
  };

  const handleValidate = (doc) => wrapBusy(doc.id, () => ceeApi.validateDocument(dossier.id, doc.id));
  const handleSupersede = (doc) => wrapBusy(doc.id, () => ceeApi.supersedeDocument(dossier.id, doc.id));
  const handleUpload = (typeKey, file) => wrapBusy(`upload-${typeKey}`, () => ceeApi.uploadDocument(dossier.id, typeKey, file));

  const openReject = (doc, label) => setRejectModal({ open: true, doc, label });
  const confirmReject = async (reason) => {
    const { doc } = rejectModal;
    setRejectModal({ open: false, doc: null, label: '' });
    if (doc) await wrapBusy(doc.id, () => ceeApi.rejectDocument(dossier.id, doc.id, reason));
  };

  return (
    <section id="docs-section" className="mb-6">
      <h2 className="text-lg font-bold text-[#0f172a] mb-3">Documents</h2>
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-2 mb-3 text-sm">
          {error}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {DOC_TYPE_META.filter((meta) => {
          // Facture = AM_ENERGIE uniquement (autres mandataires editent hors-app)
          if (meta.key === 'facture') {
            return dossier?.mandataire?.code === 'AM_ENERGIE';
          }
          return true;
        }).map((meta) => {
          const doc = getDocByType(meta.key);
          const downloadUrl = doc ? ceeApi.downloadDocumentUrl(dossier.id, doc.id) : '';
          // Process Jm 06/05 etape 5 : un partner (readOnly=true cote UI)
          // peut valider/rejeter Devis/Facture/AH (le backend verifie le scope).
          const canValidateAsPartner = readOnly
            && ['devis', 'ah', 'facture'].includes(meta.key);
          return (
            <DocCard
              key={meta.key}
              typeKey={meta.key}
              typeLabel={meta.label}
              typeDescription={meta.description}
              doc={doc}
              downloadUrl={downloadUrl}
              isPreviewing={previewKey === meta.key}
              busy={busyDocId === (doc?.id || `upload-${meta.key}`)}
              onValidate={() => doc && handleValidate(doc)}
              onReject={() => doc && openReject(doc, meta.label)}
              onSupersede={() => doc && handleSupersede(doc)}
              onUpload={(file) => handleUpload(meta.key, file)}
              onPreviewToggle={() => setPreviewKey((k) => (k === meta.key ? null : meta.key))}
              readOnly={readOnly}
              canValidateAsPartner={canValidateAsPartner}
            />
          );
        })}
      </div>

      <RejectModal
        isOpen={rejectModal.open}
        docLabel={rejectModal.label}
        onConfirm={confirmReject}
        onCancel={() => setRejectModal({ open: false, doc: null, label: '' })}
      />
    </section>
  );
}
